import React, { useRef } from 'react';
import { Upload } from 'lucide-react';

interface FileUploadProps {
  value?: File[];
  onChange?: (files: File[]) => void;
  className?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({ value, onChange, className }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleRemove = (index: number) => {
    if (!value) return;
    onChange?.(value.filter((_, i) => i !== index));
  };

  const formatSize = (bytes: number) => {
    const kb = Math.round(bytes / 1024);
    if (kb < 1024) return `${Math.max(kb, 1)} KB`;
    const mb = (bytes / (1024 * 1024)).toFixed(1);
    return `${mb} MB`;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    onChange?.([...(value ?? []), ...dropped]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    onChange?.([...(value ?? []), ...selected]);
    e.target.value = '';
  };

  return (
    <div className={`w-full ${className ?? ''}`}>
      <div
        className="flex flex-col items-center justify-between py-[10px] max-[1535px]:py-[6px] rounded-[26px] border-2 border-dashed border-[#124093] gap-[6px]"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <Upload className="text-[#124093] max-[1535px]:hidden" size={46} />

        <p className="font-['Roboto'] text-[13px] font-medium text-center text-[#124093] px-2 leading-[1.17]">
          Datei auswählen oder in das Feld ziehen
        </p>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="border border-[#124093] text-[#124093] font-['Roboto'] text-[12px] font-medium rounded-[16px] px-[33px] h-[32px] bg-transparent hover:bg-[#124093]/5 transition-colors"
        >
          Datei durchsuchen
        </button>

        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleChange}
        />
      </div>

      {value && value.length > 0 && (
        <ul className="w-full mt-3 flex flex-col gap-2">
          {value.map((f, i) => {
            const size = formatSize(f.size);

            return (
              <li
                key={`${f.name}-${i}`}
                className="min-h-[68px] rounded-[18px] bg-[#EEF1F7] px-3 py-2 flex items-center gap-3"
              >
                <svg width="26" height="25" viewBox="0 0 26 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_329_467)">
                    <path d="M13.0684 0.566406H17.0965V1.36763C17.0965 5.44695 20.4594 8.75391 24.6077 8.75402H25.4224V17.8865C25.4223 21.3382 22.5769 24.1365 19.0669 24.1365H13.0684C9.55845 24.1365 6.71301 21.3382 6.71289 17.8865V6.81641C6.71289 3.36463 9.55839 0.566406 13.0684 0.566406ZM24.6048 7.61709C21.0968 7.6155 18.2533 4.81963 18.2521 1.36985L24.6048 7.61709Z" fill="#EEF1F7" stroke="#CBD0DC" stroke-width="2" />
                    <path d="M13.4837 10.918H4.04444C1.81076 10.918 0 12.6987 0 14.8952V16.9956C0 19.1922 1.81076 20.9729 4.04444 20.9729H13.4837C15.7173 20.9729 17.5281 19.1922 17.5281 16.9956V14.8952C17.5281 12.6987 15.7173 10.918 13.4837 10.918Z" fill="#D82042" />
                    <path d="M1.17773 18.6016V13.2297H3.12486C3.54985 13.2297 3.90193 13.3057 4.18111 13.4579C4.46029 13.61 4.66923 13.8181 4.80792 14.0822C4.94662 14.3444 5.01598 14.64 5.01598 14.9688C5.01598 15.2992 4.94574 15.5965 4.80526 15.8606C4.66656 16.1228 4.45673 16.331 4.17578 16.4848C3.8966 16.6369 3.5454 16.713 3.12219 16.713H1.78321V16.0258H3.04751C3.31601 16.0258 3.53385 15.9803 3.701 15.8894C3.86814 15.7967 3.99084 15.6708 4.06908 15.5117C4.14733 15.3526 4.18644 15.1716 4.18644 14.9688C4.18644 14.7659 4.14733 14.5858 4.06908 14.4284C3.99084 14.271 3.86726 14.1477 3.69833 14.0585C3.53118 13.9694 3.31068 13.9248 3.03684 13.9248H2.00193V18.6016H1.17773ZM10.3913 13.2297V18.6016H9.63374L6.85705 14.6618H6.80638V18.6016H5.98218V13.2297H6.74502L9.52436 17.1747H9.57504V13.2297H10.3913ZM15.2904 14.9268C15.2388 14.7676 15.1695 14.6251 15.0824 14.4992C14.997 14.3715 14.8948 14.2631 14.7756 14.174C14.6565 14.083 14.5205 14.014 14.3675 13.9668C14.2164 13.9195 14.0501 13.8959 13.8687 13.8959C13.5611 13.8959 13.2837 13.9737 13.0366 14.1294C12.7894 14.285 12.5938 14.5132 12.4497 14.814C12.3075 15.113 12.2363 15.4793 12.2363 15.913C12.2363 16.3484 12.3084 16.7165 12.4524 17.0173C12.5964 17.3181 12.7938 17.5463 13.0445 17.7019C13.2953 17.8575 13.5807 17.9353 13.9007 17.9353C14.1977 17.9353 14.4564 17.8759 14.6769 17.757C14.8992 17.6381 15.0708 17.4702 15.1917 17.2534C15.3144 17.0348 15.3758 16.7777 15.3758 16.4822L15.5891 16.5215H14.0261V15.8527H16.1733V16.4639C16.1733 16.915 16.0755 17.3067 15.8799 17.6389C15.6861 17.9694 15.4175 18.2248 15.0744 18.4049C14.7329 18.5849 14.3418 18.675 13.9007 18.675C13.4064 18.675 12.9725 18.5631 12.5991 18.3393C12.2274 18.1155 11.9376 17.7981 11.7296 17.3872C11.5215 16.9744 11.4175 16.4848 11.4175 15.9182C11.4175 15.4898 11.4779 15.1051 11.5989 14.7641C11.7198 14.4231 11.8896 14.1337 12.1083 13.8959C12.3288 13.6564 12.5876 13.4736 12.8845 13.3477C13.1833 13.2201 13.5095 13.1562 13.8634 13.1562C14.1586 13.1562 14.4333 13.1991 14.6876 13.2848C14.9436 13.3705 15.1713 13.492 15.3704 13.6494C15.5714 13.8068 15.7376 13.9939 15.8692 14.2107C16.0008 14.4258 16.0897 14.6645 16.1359 14.9268H15.2904Z" fill="white" />
                  </g>
                  <defs>
                    <clipPath id="clip0_329_467">
                      <rect width="26" height="25" fill="white" />
                    </clipPath>
                  </defs>
                </svg>


                <div className="min-w-0 flex-1 leading-tight">
                  <p className="font-['Roboto'] text-[16px] font-medium text-[#3A4150] truncate">{f.name}</p>
                  <p className="font-['Roboto'] text-[14px] text-[#9CA3B1]">
                    {size} of {size}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  className="h-8 w-8 shrink-0 flex items-center justify-center"
                  aria-label={`Datei ${f.name} entfernen`}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.9997 6.73046C20.9797 6.73046 20.9497 6.73046 20.9197 6.73046C15.6297 6.20046 10.3497 6.00046 5.11967 6.53046L3.07967 6.73046C2.65967 6.77046 2.28967 6.47046 2.24967 6.05046C2.20967 5.63046 2.50967 5.27046 2.91967 5.23046L4.95967 5.03046C10.2797 4.49046 15.6697 4.70046 21.0697 5.23046C21.4797 5.27046 21.7797 5.64046 21.7397 6.05046C21.7097 6.44046 21.3797 6.73046 20.9997 6.73046Z" fill="#292D32" />
                    <path d="M8.49977 5.72C8.45977 5.72 8.41977 5.72 8.36977 5.71C7.96977 5.64 7.68977 5.25 7.75977 4.85L7.97977 3.54C8.13977 2.58 8.35977 1.25 10.6898 1.25H13.3098C15.6498 1.25 15.8698 2.63 16.0198 3.55L16.2398 4.85C16.3098 5.26 16.0298 5.65 15.6298 5.71C15.2198 5.78 14.8298 5.5 14.7698 5.1L14.5498 3.8C14.4098 2.93 14.3798 2.76 13.3198 2.76H10.6998C9.63977 2.76 9.61977 2.9 9.46977 3.79L9.23977 5.09C9.17977 5.46 8.85977 5.72 8.49977 5.72Z" fill="#292D32" />
                    <path d="M15.2104 22.7515H8.79039C5.30039 22.7515 5.16039 20.8215 5.05039 19.2615L4.40039 9.19154C4.37039 8.78154 4.69039 8.42154 5.10039 8.39154C5.52039 8.37154 5.87039 8.68154 5.90039 9.09154L6.55039 19.1615C6.66039 20.6815 6.70039 21.2515 8.79039 21.2515H15.2104C17.3104 21.2515 17.3504 20.6815 17.4504 19.1615L18.1004 9.09154C18.1304 8.68154 18.4904 8.37154 18.9004 8.39154C19.3104 8.42154 19.6304 8.77154 19.6004 9.19154L18.9504 19.2615C18.8404 20.8215 18.7004 22.7515 15.2104 22.7515Z" fill="#292D32" />
                    <path d="M13.6601 17.25H10.3301C9.92008 17.25 9.58008 16.91 9.58008 16.5C9.58008 16.09 9.92008 15.75 10.3301 15.75H13.6601C14.0701 15.75 14.4101 16.09 14.4101 16.5C14.4101 16.91 14.0701 17.25 13.6601 17.25Z" fill="#292D32" />
                    <path d="M14.5 13.25H9.5C9.09 13.25 8.75 12.91 8.75 12.5C8.75 12.09 9.09 11.75 9.5 11.75H14.5C14.91 11.75 15.25 12.09 15.25 12.5C15.25 12.91 14.91 13.25 14.5 13.25Z" fill="#292D32" />
                  </svg>

                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
